import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Layout from './components/layout/Layout';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import CaseStudiesPage from './pages/CaseStudiesPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import AWSServicesPage from './pages/AWSServicesPage';
import BlogPage from './pages/BlogPage';
import BlogDetail from './pages/BlogDetail';
import CareersPage from './pages/CareersPage';
import DevelopmentIndustriesPage from './pages/DevelopmentIndustriesPage';
import DevelopmentPage from './pages/DevelopmentPage';
import InsightsPage from './pages/InsightsPage';
import StartupPage from './pages/StartupPage';
import InvestorInsightsPage from './pages/InvestorInsightsPage';
import InvestorInsightDetail from './pages/InvestorInsightDetail';
import NotFoundPage from './pages/NotFoundPage';
import ScrollToTop from './components/utils/ScrollToTop';
import { initGA, logPageView } from './utils/analytics';
import { useParams } from 'react-router-dom';

import AIServicePage from './pages/AIServicePage';
import AIServicesHubPage from './pages/AIServicesHubPage';
import ServiceDetail from './pages/ServiceDetail';
import contentData from './data/contentData.json';

// Component to route AI services vs regular services
const AIServiceOrServiceDetail = () => {
  const { id } = useParams();
  
  // Check if this is an AI service ID
  const isAIService = contentData.aiServices?.some(service => service.id === id);
  
  if (isAIService) {
    return <AIServicePage />;
  }
  
  return <ServiceDetail />;
};

// Analytics wrapper component to track route changes
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Initialize Google Analytics on first mount
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (measurementId) {
      initGA(measurementId);
    }
  }, []);

  useEffect(() => {
    // Track page view on route change
    logPageView();
  }, [location]);

  return null;
}

function App() {
  return (
    <Router>
      <AnalyticsTracker />
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="services/ai" element={<AIServicesHubPage />} />
            <Route path="services/ai/:id" element={<AIServicePage />} />
            <Route path="services/aws-services" element={<ServicesPage />} />
            <Route path="services/aws-services/:id" element={<ServiceDetail />} />
            <Route path="services/aws" element={<AWSServicesPage />} />
            <Route path="development" element={<DevelopmentPage />} />
            <Route path="development/:id" element={<AIServiceOrServiceDetail />} />
            <Route path="development/industries" element={<DevelopmentIndustriesPage />} />
            <Route path="insights" element={<InsightsPage />} />
            <Route path="startup" element={<StartupPage />} />
            <Route path="investor-insights" element={<InvestorInsightsPage />} />
            <Route path="investor-insights/:id" element={<InvestorInsightDetail />} />
            <Route path="case-studies" element={<CaseStudiesPage />} />
            <Route path="blog" element={<BlogPage />} />
            <Route path="blog/:id" element={<BlogDetail />} />
            <Route path="contact" element={<ContactPage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="careers" element={<CareersPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </Router>
  );
}

export default App;
